import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiMaximize, FiMinimize } from 'react-icons/fi';
import useReportLift from './useReportLift';
import useReportOverlay from './useReportOverlay';

let nextReportId = 0;

export default function ExpandableReport({ title, className = '', children, onExpand, collapsed = false, id, ...props }) {
    const [dashboard, setDashboard] = useState(null);
    const [reportId] = useState(() => id || `portal-expandable-report-${++nextReportId}`);
    const reportName = /report$/i.test(title) ? title.toLowerCase() : `${title.toLowerCase()} report`;
    const card = useRef(null), slot = useRef(null), trigger = useRef(null);
    const { phase, expanded, expand, restore, geometry } = useReportLift(card, slot, onExpand);
    useReportOverlay(expanded, restore, card, trigger);
    useEffect(() => { setDashboard(card.current?.closest('.portal-dashboard') || null); }, []);

    const controls = dashboard && <button ref={trigger} type="button" className={`portal-report-expand${expanded ? ' is-expanded' : ''}`}
        aria-label={expanded ? `Restore ${reportName}` : `Expand ${reportName}`}
        aria-expanded={expanded} aria-controls={reportId} disabled={phase === 'closing'}
        title={expanded ? 'Back to dashboard' : 'Expand report'} onClick={expanded ? restore : expand}>
        {expanded ? <FiMinimize aria-hidden="true" /> : <FiMaximize aria-hidden="true" />}
        {expanded && <span>Back to dashboard</span>}
    </button>;

    return <div ref={slot} className={`portal-report-slot${expanded ? ' is-lifted' : collapsed ? ' is-collapsed' : ''}`} style={expanded ? { height: geometry.origin.height } : undefined}>
        {expanded && createPortal(<div className={`portal-report-backdrop is-${phase}`} role="presentation" onClick={restore} />, document.body)}
        <section {...props} ref={card} id={reportId} aria-label={title}
            role={expanded ? 'dialog' : undefined} aria-modal={expanded ? true : undefined}
            style={expanded ? geometry.destination : undefined}
            className={`portal-expandable-report ${className}${expanded ? ' is-expanded' : ''} is-${phase}`}>
            {children({ expanded, controls, expand, canExpand: !!dashboard })}
        </section>
    </div>;
}
